import { useState } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  Heading,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { Drawer } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Search, X, ChevronDown } from 'lucide-react'
import { useProperties } from '@/features/properties/hooks/useProperties'
import type { DocumentCategory, DocumentFilters } from '../types'

interface DocumentSearchDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSearch: (filters: DocumentFilters) => void
  currentFilters?: DocumentFilters
}

export function DocumentSearchDrawer({
  isOpen,
  onClose,
  onSearch,
  currentFilters = {},
}: DocumentSearchDrawerProps) {
  const { t } = useTranslation()
  const { data: properties = [] } = useProperties()

  const [search, setSearch] = useState(currentFilters.search || '')
  const [residenceId, setResidenceId] = useState(currentFilters.residence_id || '')
  const [category, setCategory] = useState<DocumentCategory | ''>(
    (currentFilters.category as DocumentCategory) || ''
  )
  const [dateFrom, setDateFrom] = useState(currentFilters.date_from || '')
  const [dateTo, setDateTo] = useState(currentFilters.date_to || '')

  const handleSearch = () => {
    const filters: DocumentFilters = {
      ...(search && { search }),
      ...(residenceId && { residence_id: residenceId }),
      ...(category && { category: category as DocumentCategory }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    }
    onSearch(filters)
    onClose()
  }

  const handleReset = () => {
    setSearch('')
    setResidenceId('')
    setCategory('')
    setDateFrom('')
    setDateTo('')
    onSearch({})
    onClose()
  }

  const hasActiveFilters =
    search || residenceId || category || dateFrom || dateTo

  const handleInteractOutside = () => {
    onClose()
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="md"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px">
            <Heading size="lg">Rechercher des documents</Heading>
            <Drawer.CloseTrigger onClick={onClose} />
          </Drawer.Header>

          <Drawer.Body>
          <VStack gap={4} align="stretch">
            {/* Text Search */}
            <Field.Root>
              <Field.Label>Recherche textuelle</Field.Label>
              <HStack position="relative">
                <Search
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    color: '#718096',
                    zIndex: 1,
                  }}
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nom du document..."
                  pl="40px"
                />
              </HStack>
              <Field.HelperText>
                Recherche dans le nom des documents
              </Field.HelperText>
            </Field.Root>

            {/* Residence Filter */}
            <Field.Root>
              <Field.Label>Résidence</Field.Label>
              <NativeSelectRoot position="relative">
                <NativeSelectField
                  value={residenceId}
                  onChange={(e) => setResidenceId(e.target.value)}
                  paddingRight="10"
                >
                  <option value="">Toutes les résidences</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.residence_key
                        ? `${property.residence_key} - ${property.name}`
                        : property.name}
                    </option>
                  ))}
                </NativeSelectField>
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  color="gray.500"
                >
                  <ChevronDown size={16} />
                </Box>
              </NativeSelectRoot>
            </Field.Root>

            {/* Category Filter */}
            <Field.Root>
              <Field.Label>Catégorie</Field.Label>
              <NativeSelectRoot position="relative">
                <NativeSelectField
                  value={category}
                  onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                  paddingRight="10"
                >
                  <option value="">Toutes les catégories</option>
                  <option value="constitutif">{t('documents.categories.constitutif')}</option>
                  <option value="ag">{t('documents.categories.ag')}</option>
                  <option value="comptabilite">{t('documents.categories.comptabilite')}</option>
                  <option value="travaux">{t('documents.categories.travaux')}</option>
                  <option value="technique">{t('documents.categories.technique')}</option>
                  <option value="contrats">{t('documents.categories.contrats')}</option>
                  <option value="sinistres">{t('documents.categories.sinistres')}</option>
                  <option value="juridique">{t('documents.categories.juridique')}</option>
                  <option value="correspondance">
                    {t('documents.categories.correspondance')}
                  </option>
                  <option value="coproprietaire">{t('documents.categories.coproprietaire')}</option>
                  <option value="autre">{t('documents.categories.autre')}</option>
                </NativeSelectField>
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  color="gray.500"
                >
                  <ChevronDown size={16} />
                </Box>
              </NativeSelectRoot>
            </Field.Root>

            {/* Date Range */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Période
              </Text>
              <HStack gap={2}>
                <Field.Root flex={1}>
                  <Field.Label fontSize="xs">Du</Field.Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    size="sm"
                  />
                </Field.Root>
                <Field.Root flex={1}>
                  <Field.Label fontSize="xs">Au</Field.Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    size="sm"
                  />
                </Field.Root>
              </HStack>
            </Box>

            {/* Active Filters Info */}
            {hasActiveFilters && (
              <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                <Text fontSize="sm" fontWeight="medium" color="blue.700">
                  Filtres actifs
                </Text>
                <VStack align="start" gap={1} mt={2}>
                  {search && (
                    <Text fontSize="xs" color="blue.600">
                      • Recherche : "{search}"
                    </Text>
                  )}
                  {residenceId && (
                    <Text fontSize="xs" color="blue.600">
                      • Résidence sélectionnée
                    </Text>
                  )}
                  {category && (
                    <Text fontSize="xs" color="blue.600">
                      • Catégorie : {t(`documents.categories.${category}`)}
                    </Text>
                  )}
                  {(dateFrom || dateTo) && (
                    <Text fontSize="xs" color="blue.600">
                      • Période définie
                    </Text>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
          </Drawer.Body>

          <Drawer.Footer borderTopWidth="1px">
            <HStack gap={2} w="full">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasActiveFilters}
                flex={1}
              >
                <X size={16} />
                <Text ml={2}>Réinitialiser</Text>
              </Button>
              <Button colorPalette="brand" onClick={handleSearch} flex={1}>
                <Search size={16} />
                <Text ml={2}>Rechercher</Text>
              </Button>
            </HStack>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
